import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Bike, MapPin, Phone, Mail, RefreshCw, Send, CheckCircle2, AlertCircle, Truck, Settings, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const STATUS_META = {
  pending:         { label: "Pendiente",     color: "bg-amber-500",   text: "text-amber-700",   bg: "bg-amber-50",   border: "border-amber-300" },
  payment_sent:    { label: "Cobro enviado", color: "bg-blue-500",    text: "text-blue-700",    bg: "bg-blue-50",    border: "border-blue-300" },
  paid:            { label: "Pagado",        color: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-300" },
  preparing:       { label: "Preparando",    color: "bg-violet-500",  text: "text-violet-700",  bg: "bg-violet-50",  border: "border-violet-300" },
  out_for_delivery:{ label: "En camino",     color: "bg-indigo-500",  text: "text-indigo-700",  bg: "bg-indigo-50",  border: "border-indigo-300" },
  delivered:       { label: "Entregado",     color: "bg-emerald-700", text: "text-emerald-800", bg: "bg-emerald-100",border: "border-emerald-400" },
  cancelled:       { label: "Cancelado",     color: "bg-rose-500",    text: "text-rose-700",    bg: "bg-rose-50",    border: "border-rose-300" },
};

const NEXT_STATUS = {
  pending: "payment_sent",
  payment_sent: "paid",
  paid: "preparing",
  preparing: "out_for_delivery",
  out_for_delivery: "delivered",
};

const formatCurrency = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n || 0));

export default function AdminDelivery() {
  const { user } = useAuth();
  const expired = user?.subscription_active === false;
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active"); // active | all | history
  const [editing, setEditing] = useState(null);   // order currently being edited (modal)
  const [editForm, setEditForm] = useState({ delivery_fee: 0, discount: 0 });
  const [chargingId, setChargingId] = useState(null);
  const previousIdsRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/delivery/orders");
      setOrders(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh + detect new orders since last poll.
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const { data: fresh } = await api.get("/admin/delivery/orders");
        const prev = previousIdsRef.current;
        if (prev) {
          const newOnes = fresh.filter((o) => !prev.has(o.id));
          if (newOnes.length > 0) {
            try {
              const AC = window.AudioContext || window.webkitAudioContext;
              const ctx = new AC();
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.connect(gain); gain.connect(ctx.destination);
              osc.frequency.value = 880; gain.gain.value = 0.18;
              osc.start(); osc.stop(ctx.currentTime + 0.18);
              setTimeout(() => { try { ctx.close(); } catch (_) {/* ignore */} }, 400);
            } catch (_) { /* audio may be blocked, that's fine */ }
            newOnes.forEach((o) => {
              toast.success(`Nuevo pedido ${o.order_number} · ${o.customer_name}`, { duration: 8000 });
            });
          }
        }
        previousIdsRef.current = new Set(fresh.map((o) => o.id));
        setOrders(fresh);
      } catch (_) { /* silent */ }
    }, 20_000);
    return () => clearInterval(id);
  }, []);

  // Initialize the snapshot of known order IDs once orders are loaded the first time.
  useEffect(() => {
    if (orders.length > 0 && previousIdsRef.current === null) {
      previousIdsRef.current = new Set(orders.map((o) => o.id));
    }
  }, [orders]);

  const filtered = orders.filter((o) => {
    if (filter === "all") return true;
    if (filter === "history") return o.status === "delivered" || o.status === "cancelled";
    return o.status !== "delivered" && o.status !== "cancelled";
  });

  const openEdit = (o) => {
    setEditing(o);
    setEditForm({ delivery_fee: o.delivery_fee || 0, discount: o.discount || 0 });
  };

  const saveEdit = async () => {
    try {
      const fee = parseFloat(editForm.delivery_fee || 0);
      const disc = parseFloat(editForm.discount || 0);
      const { data } = await api.patch(`/admin/delivery/orders/${editing.id}`, {
        delivery_fee: isNaN(fee) ? 0 : fee,
        discount: isNaN(disc) ? 0 : disc,
      });
      setOrders((arr) => arr.map((x) => (x.id === data.id ? data : x)));
      setEditing(null);
      toast.success("Costo de envío actualizado");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setStatus = async (o, newStatus) => {
    try {
      const { data } = await api.patch(`/admin/delivery/orders/${o.id}`, { status: newStatus });
      setOrders((arr) => arr.map((x) => (x.id === data.id ? data : x)));
      toast.success(`Pedido marcado como ${STATUS_META[newStatus]?.label || newStatus}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const charge = (o) => {
    // Open WhatsApp SYNCHRONOUSLY first (browsers block window.open after async await).
    let wa = null;
    if (o.whatsapp_link) {
      wa = window.open(o.whatsapp_link, "_blank");
    }
    // Then perform the async work (send email + update status).
    setChargingId(o.id);
    (async () => {
      try {
        await api.post(`/admin/delivery/orders/${o.id}/charge`);
        toast.success(`Email enviado a ${o.customer_email}`);
        if (!o.whatsapp_link) {
          toast.info("Configura el teléfono del cliente y tu link de cobro para enviar por WhatsApp.");
        } else if (!wa) {
          toast.info("Toca el botón verde 'WhatsApp' para abrirlo.");
        }
        await load();
      } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
      finally { setChargingId(null); }
    })();
  };

  const openWhatsApp = (o) => {
    // Synchronous open so iOS Safari / strict popup blockers don't kill it.
    if (!o.whatsapp_link) {
      toast.error("El cliente no tiene teléfono o falta el link de cobro en Ajustes.");
      return;
    }
    const w = window.open(o.whatsapp_link, "_blank");
    if (!w) toast.info("Abre WhatsApp manualmente — tu navegador bloqueó la ventana.");
  };

  return (
    <div className="animate-fade-up" data-testid="admin-delivery-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#7248ae] mb-2 inline-flex items-center gap-2">
            <Bike className="h-3 w-3" />Pedidos a domicilio
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#1a0f2e] tracking-tight">Delivery</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={load} className="border-[#b182ec]/50" data-testid="delivery-refresh">
            <RefreshCw className="h-4 w-4 mr-1" />Actualizar
          </Button>
          <Button variant="outline" asChild className="border-[#b182ec]/50">
            <Link to="/admin/settings" data-testid="delivery-go-settings"><Settings className="h-4 w-4 mr-1" />Configurar</Link>
          </Button>
        </div>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap" data-testid="delivery-filters">
        {[
          { key: "active",  label: "Activos" },
          { key: "history", label: "Historial" },
          { key: "all",     label: "Todos" },
        ].map((f) => (
          <button key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${filter === f.key ? "bg-[#3e166c] text-white" : "text-[#3e166c] bg-white border border-[#b182ec]/40 hover:bg-[#3e166c]/10"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-[#7248ae]">Cargando…</p>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-[#b182ec]/40">
          <Bike className="h-10 w-10 mx-auto text-[#b182ec] mb-3" />
          <p className="text-[#7248ae]">No hay pedidos {filter === "history" ? "en el historial" : "activos"} todavía.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="delivery-list">
          {filtered.map((o) => {
            const m = STATUS_META[o.status] || STATUS_META.pending;
            const next = NEXT_STATUS[o.status];
            return (
              <Card key={o.id} className={`p-5 border ${m.border} rounded-2xl`} data-testid={`delivery-card-${o.order_number}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#7248ae]">{o.order_number}</p>
                    <h3 className="font-display text-xl font-bold text-[#1a0f2e]">{o.customer_name}</h3>
                  </div>
                  <Badge className={`${m.color} text-white`} data-testid={`delivery-status-${o.order_number}`}>{m.label}</Badge>
                </div>

                <div className="space-y-1 text-xs text-[#7248ae] mb-3">
                  <p className="flex items-start gap-1.5"><MapPin className="h-3 w-3 mt-0.5 flex-shrink-0" /><span>{o.delivery_address}{o.delivery_references ? ` · ${o.delivery_references}` : ""}</span></p>
                  <p className="flex items-center gap-1.5"><Phone className="h-3 w-3" />{o.customer_phone}</p>
                  <p className="flex items-center gap-1.5"><Mail className="h-3 w-3" />{o.customer_email}</p>
                </div>

                <div className="rounded-lg bg-[#fcfbfd] border border-[#b182ec]/30 p-3 mb-3 text-sm" data-testid={`delivery-items-${o.order_number}`}>
                  {o.items.map((it, idx) => (
                    <div key={idx} className="flex justify-between text-[#1a0f2e]">
                      <span>{it.qty} × {it.name}</span>
                      <span className="text-[#3e166c] font-semibold">{formatCurrency(it.line_total)}</span>
                    </div>
                  ))}
                  <div className="border-t border-[#b182ec]/30 mt-2 pt-2 space-y-0.5">
                    <div className="flex justify-between text-xs text-[#7248ae]">
                      <span>Subtotal</span><span>{formatCurrency(o.subtotal)}</span>
                    </div>
                    {o.delivery_fee > 0 && (
                      <div className="flex justify-between text-xs text-[#7248ae]"><span>Envío</span><span>{formatCurrency(o.delivery_fee)}</span></div>
                    )}
                    {o.discount > 0 && (
                      <div className="flex justify-between text-xs text-emerald-700"><span>Descuento</span><span>- {formatCurrency(o.discount)}</span></div>
                    )}
                    <div className="flex justify-between text-base font-bold text-[#1a0f2e] pt-1">
                      <span>Total</span><span>{formatCurrency(o.total)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {o.status === "pending" && (
                    <Button size="sm" variant="outline" onClick={() => openEdit(o)} disabled={expired} className="border-[#b182ec]/50" data-testid={`delivery-edit-${o.order_number}`}>
                      <Truck className="h-3 w-3 mr-1" />Envío / descuento
                    </Button>
                  )}
                  {(o.status === "pending" || o.status === "payment_sent") && (
                    <Button
                      size="sm"
                      onClick={() => charge(o)}
                      disabled={expired || chargingId === o.id}
                      className="bg-[#3e166c] hover:bg-[#7248ae] text-white"
                      data-testid={`delivery-charge-${o.order_number}`}
                    >
                      <Send className="h-3 w-3 mr-1" />{o.status === "payment_sent" ? "Reenviar cobro" : "Cobrar"}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openWhatsApp(o)}
                    disabled={expired || !o.customer_phone}
                    className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                    data-testid={`delivery-whatsapp-${o.order_number}`}
                  >
                    <MessageCircle className="h-3 w-3 mr-1" />WhatsApp
                  </Button>
                  {next && (
                    <Button size="sm" variant="outline" onClick={() => setStatus(o, next)} disabled={expired} className="border-[#b182ec]/50" data-testid={`delivery-next-${o.order_number}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" />{STATUS_META[next]?.label}
                    </Button>
                  )}
                  {o.status !== "cancelled" && o.status !== "delivered" && (
                    <Button size="sm" variant="ghost" onClick={() => setStatus(o, "cancelled")} disabled={expired} className="text-rose-600 hover:bg-rose-50" data-testid={`delivery-cancel-${o.order_number}`}>
                      <AlertCircle className="h-3 w-3 mr-1" />Cancelar
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white" data-testid="delivery-edit-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1a0f2e]">Costo de envío y descuento</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-[#7248ae]">Pedido <strong>{editing.order_number}</strong> · Subtotal {formatCurrency(editing.subtotal)}</p>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 block">Costo de envío (MXN)</label>
                <Input type="number" min="0" step="0.01" value={editForm.delivery_fee}
                  onChange={(e) => setEditForm({ ...editForm, delivery_fee: e.target.value })}
                  data-testid="edit-delivery-fee-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#7248ae] mb-2 block">Descuento (MXN)</label>
                <Input type="number" min="0" step="0.01" value={editForm.discount}
                  onChange={(e) => setEditForm({ ...editForm, discount: e.target.value })}
                  data-testid="edit-discount-input" />
              </div>
              <p className="text-sm font-bold text-[#1a0f2e]">
                Nuevo total: {formatCurrency(Math.max(0, Number(editing.subtotal || 0) + Number(editForm.delivery_fee || 0) - Number(editForm.discount || 0)))}
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditing(null)} className="border-[#b182ec]/50">Cancelar</Button>
            <Button onClick={saveEdit} className="bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid="edit-save-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
